import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { SessionStatusBadge, canCancelSession } from "@/lib/attendance";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
    ArrowLeft, Check, Clock, X, Lock, Save, CheckCircle2, XCircle, Users, ShieldAlert,
} from "lucide-react";

const OPTIONS = [
    { value: "present", label: "Present", icon: Check, active: "bg-[#16A34A] text-white border-[#16A34A]" },
    { value: "late", label: "Late", icon: Clock, active: "bg-[#FFA81B] text-white border-[#FFA81B]" },
    { value: "absent", label: "Absent", icon: X, active: "bg-[#E11D48] text-white border-[#E11D48]" },
];

export default function SessionRoster() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [marks, setMarks] = useState({});      // student_id -> status (local, unsaved)
    const [saving, setSaving] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    // GET /sessions/{id} returns the raw session document, which carries ids but
    // not the batch/course names the list endpoint decorates — look them up.
    const [names, setNames] = useState({ batches: {}, courses: {} });

    const load = async () => {
        try {
            const { data } = await api.get(`/sessions/${sessionId}`);
            setData(data);
            const initial = {};
            data.roster.forEach(r => { if (r.attendance_status) initial[r.student_id] = r.attendance_status; });
            setMarks(initial);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load this session");
            navigate("/faculty/attendance");
        }
    };
    useEffect(() => { load(); }, [sessionId]); // eslint-disable-line
    useEffect(() => {
        Promise.all([
            api.get("/batches").catch(() => ({ data: { items: [] } })),
            api.get("/courses").catch(() => ({ data: { items: [] } })),
        ]).then(([b, c]) => setNames({
            batches: Object.fromEntries(b.data.items.map(x => [x.id, x.name])),
            courses: Object.fromEntries(c.data.items.map(x => [x.id, x.title])),
        }));
    }, []);

    if (!data) return <div className="text-muted-foreground">Loading…</div>;

    const { session, roster, can_edit, edit_locked_reason } = data;
    const locked = !can_edit || session.status === "cancelled";
    const markedCount = Object.keys(marks).length;
    const batchName = names.batches[session.batch_id] || "Batch";
    const courseTitle = names.courses[session.course_id];

    const setAll = (status) => {
        const next = {};
        roster.forEach(r => { if (!r.inactive) next[r.student_id] = status; });
        // Keep any existing marks for inactive students so we don't wipe history.
        roster.filter(r => r.inactive && r.attendance_status).forEach(r => { next[r.student_id] = r.attendance_status; });
        setMarks(next);
    };

    const saveMarks = async ({ thenSubmit = false } = {}) => {
        const records = Object.entries(marks).map(([student_id, status]) => ({ student_id, status }));
        if (records.length === 0) { toast.error("Mark at least one student first"); return; }
        setSaving(true);
        try {
            await api.post(`/sessions/${sessionId}/attendance`, { records });
            if (thenSubmit) {
                await api.post(`/sessions/${sessionId}/submit`, {});
                toast.success("Attendance submitted — session completed");
                navigate("/faculty/attendance");
                return;
            }
            toast.success("Attendance saved");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not save attendance");
        } finally { setSaving(false); }
    };

    const cancelSession = async () => {
        if (!cancelReason.trim()) return;
        try {
            await api.post(`/sessions/${sessionId}/cancel`, { reason: cancelReason });
            toast.success("Session cancelled — it will not count as absences");
            navigate("/faculty/attendance");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not cancel");
        }
    };

    const counts = OPTIONS.reduce((acc, o) => {
        acc[o.value] = Object.values(marks).filter(v => v === o.value).length;
        return acc;
    }, {});

    return (
        <div className="space-y-6" data-testid="session-roster-page">
            <button onClick={() => navigate("/faculty/attendance")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="roster-back-btn">
                <ArrowLeft className="h-4 w-4" /> Back to attendance
            </button>

            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                        {batchName} · {session.session_date} · {session.start_time}{session.end_time ? `–${session.end_time}` : ""}
                    </div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Mark attendance</h1>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        <SessionStatusBadge status={session.status} />
                        {courseTitle && <Badge variant="outline" className="text-[10px]">{courseTitle}</Badge>}
                        <Badge variant="outline" className="text-[10px] capitalize">{session.mode}</Badge>
                        {session.is_backdated && (
                            <Badge variant="outline" className="text-[10px] border-[#FFA81B] text-[#FFA81B]" data-testid="roster-backdated-badge">Backdated entry</Badge>
                        )}
                    </div>
                </div>
                {canCancelSession(session, can_edit) && (
                    <Button variant="ghost" className="text-[#E11D48] hover:text-[#E11D48]"
                        onClick={() => setCancelOpen(true)} data-testid="roster-cancel-btn">
                        <XCircle className="h-4 w-4 mr-2" /> Cancel session
                    </Button>
                )}
            </div>

            {session.status === "cancelled" && (
                <Card className="mx-card border-[#E11D48]/40">
                    <CardContent className="p-4 flex items-start gap-3" data-testid="cancelled-banner">
                        <XCircle className="h-5 w-5 text-[#E11D48] shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <div className="font-semibold text-[#E11D48]">This session was cancelled</div>
                            <div className="text-muted-foreground mt-0.5">
                                Reason: {session.cancel_reason || "—"}. It is excluded from all attendance calculations.
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {locked && session.status !== "cancelled" && (
                <Card className="mx-card border-[#FFA81B]/40">
                    <CardContent className="p-4 flex items-start gap-3" data-testid="edit-locked-banner">
                        <Lock className="h-5 w-5 text-[#FFA81B] shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <div className="font-semibold">Editing is locked</div>
                            <div className="text-muted-foreground mt-0.5">{edit_locked_reason}</div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Tally + bulk action */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="gap-1"><Users className="h-3 w-3" /> {roster.length} on roster</Badge>
                    <Badge className="bg-[#16A34A] hover:bg-[#16A34A]" data-testid="tally-present">{counts.present} present</Badge>
                    <Badge className="bg-[#FFA81B] hover:bg-[#FFA81B]" data-testid="tally-late">{counts.late} late</Badge>
                    <Badge className="bg-[#E11D48] hover:bg-[#E11D48]" data-testid="tally-absent">{counts.absent} absent</Badge>
                    {markedCount < roster.length && (
                        <Badge variant="outline">{roster.length - markedCount} unmarked</Badge>
                    )}
                </div>
                {!locked && (
                    <div className="flex gap-2">
                        <Button variant="outline" className="rounded-xl" onClick={() => setAll("present")} data-testid="mark-all-present-btn">
                            <CheckCircle2 className="h-4 w-4 mr-2" /> Mark all present
                        </Button>
                        <Button variant="ghost" onClick={() => setMarks({})} data-testid="clear-marks-btn">Clear</Button>
                    </div>
                )}
            </div>

            <Card className="mx-card">
                <CardContent className="p-0 divide-y divide-border">
                    {roster.map(r => (
                        <div key={r.student_id} className="flex flex-wrap items-center gap-4 p-4" data-testid={`roster-row-${r.student_id}`}>
                            <div className="h-9 w-9 rounded-full bg-brand-gradient flex items-center justify-center text-white font-bold text-sm shrink-0">
                                {r.name?.[0] || "?"}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm flex items-center gap-2">
                                    {r.name}
                                    {r.inactive && <Badge variant="outline" className="text-[10px]">Deactivated</Badge>}
                                </div>
                                <div className="text-xs text-muted-foreground font-mono">{r.enrollment_number || "—"}</div>
                                {r.edit_count > 0 && (
                                    <div className="text-[10px] text-muted-foreground mt-0.5">edited {r.edit_count}×</div>
                                )}
                            </div>
                            <div className="flex gap-1.5">
                                {OPTIONS.map(o => {
                                    const selected = marks[r.student_id] === o.value;
                                    const Icon = o.icon;
                                    return (
                                        <button
                                            key={o.value}
                                            disabled={locked}
                                            onClick={() => setMarks({ ...marks, [r.student_id]: o.value })}
                                            data-testid={`mark-${o.value}-${r.student_id}`}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all
                                                ${selected ? o.active : "border-border text-muted-foreground hover:bg-muted"}
                                                ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
                                        >
                                            <Icon className="h-3.5 w-3.5" /> {o.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    {roster.length === 0 && (
                        <div className="p-12 text-center text-muted-foreground">
                            <ShieldAlert className="h-10 w-10 mx-auto mb-3 opacity-40" />
                            No active students in this batch.
                        </div>
                    )}
                </CardContent>
            </Card>

            {!locked && (
                <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="outline" className="rounded-xl" onClick={() => saveMarks()} disabled={saving} data-testid="save-marks-btn">
                        <Save className="h-4 w-4 mr-2" /> Save without closing
                    </Button>
                    {session.status !== "completed" && (
                        <Button className="mx-btn-primary" onClick={() => saveMarks({ thenSubmit: true })} disabled={saving} data-testid="submit-session-btn">
                            <CheckCircle2 className="h-4 w-4 mr-2" /> Submit &amp; complete class
                        </Button>
                    )}
                </div>
            )}

            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cancel this session?</DialogTitle>
                        <DialogDescription>
                            A cancelled session is excluded from all attendance calculations — it will never count as an absence.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label>Reason</Label>
                        <Textarea rows={3} value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                            placeholder="e.g. faculty unwell, power outage" data-testid="roster-cancel-reason" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-xl" onClick={() => setCancelOpen(false)}>Keep it</Button>
                        <Button onClick={cancelSession} disabled={!cancelReason.trim()}
                            className="bg-[#E11D48] hover:bg-[#E11D48] text-white rounded-xl" data-testid="roster-cancel-confirm">
                            Cancel session
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
